import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { getMe, logout as apiLogout } from '../services/api';

/**
 * Timeout de inatividade da sessão, em ms. Após esse tempo sem interação
 * (mouse/teclado/touch/scroll), o usuário é deslogado por segurança.
 * 1 hora = 60 * 60 * 1000 ms.
 */
const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000;

/**
 * Eventos considerados "atividade do usuário". Usados com passive: true
 * pra não bloquear o scroll/compositor.
 */
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

/**
 * Modos de sessão — espelho do SESSION_MODES do backend.
 * Uso: comparação direta com user.mode vindo do GET /auth/me.
 */
export const SESSION_MODES = Object.freeze({
  MASTER_GLOBAL:       'MASTER_GLOBAL',
  MASTER_IMPERSONATED: 'MASTER_IMPERSONATED',
  TENANT_USER:         'TENANT_USER',
});

/**
 * Parse seguro de features — evita crash se o valor for JSON malformado.
 * Reutilizável em qualquer componente que precise ler features de tenant/plan.
 */
export function safeParseFeatures(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return {}; }
}

/**
 * Parse seguro genérico — para arrays, configs, options, etc.
 * Retorna fallback em caso de JSON malformado.
 */
export function safeParseJSON(raw, fallback = null) {
  if (raw === null || raw === undefined) return fallback;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return fallback; }
}

/**
 * Escurece uma cor hex em N% — usado para gerar a variação --primary-dark
 * a partir da primary_color do cliente. Não usa secondary_color (que é
 * uma cor complementar, não uma variação escura).
 */
export function darkenHex(hex, percent = 15) {
  if (!hex || typeof hex !== 'string') return hex;
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return hex;
  const num = parseInt(clean, 16);
  if (isNaN(num)) return hex;
  const amount = Math.round(255 * (percent / 100));
  const r = Math.max(0, ((num >> 16) & 0xff) - amount);
  const g = Math.max(0, ((num >> 8) & 0xff) - amount);
  const b = Math.max(0, (num & 0xff) - amount);
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}

/**
 * Aplica branding (primary + dark variant) ao :root.
 * Reutilizável em TenantLayout, LoginPage, PublicBooking, etc.
 */
export function applyTenantBranding(primaryColor) {
  if (!primaryColor) return;
  const root = document.documentElement.style;
  root.setProperty('--primary', primaryColor);
  root.setProperty('--primary-dark', darkenHex(primaryColor, 15));
  root.setProperty('--primary-light', primaryColor + '20'); // 20 = ~12% alpha hex
  root.setProperty('--primary-faint', primaryColor + '10');
}

export function clearTenantBranding() {
  const root = document.documentElement.style;
  root.removeProperty('--primary');
  root.removeProperty('--primary-dark');
  root.removeProperty('--primary-light');
  root.removeProperty('--primary-faint');
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimerRef = useRef(null);

  const loadUser = useCallback(async () => {
    try {
      const res = await getMe();
      setUser(res.data.user);
    } catch {
      setUser(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const loginUser = (token, userData) => {
    // Store token for master-token restoration after impersonation
    if (token) localStorage.setItem('atendo_user_token', token);
    // Fallback: normalizar mode se ausente (defense-in-depth — buildLoginPayload já inclui mode)
    if (!userData.mode) {
      userData.mode = userData.is_master ? SESSION_MODES.MASTER_GLOBAL : SESSION_MODES.TENANT_USER;
    }
    setUser(userData);
  };

  const logout = async () => {
    try { await apiLogout(); } catch {}
    setUser(null);
    window.location.href = '/login';
  };

  /**
   * Logout forçado por inatividade — mesma mecânica do logout manual mas
   * mostra um toast avisando o usuário e grava flag pra LoginPage exibir
   * mensagem apropriada.
   */
  const logoutInactivity = useCallback(async () => {
    try { await apiLogout(); } catch {}
    try { sessionStorage.setItem('atendo_logout_reason', 'inactivity'); } catch {}
    try { toast('Sessão encerrada por inatividade. Faça login novamente.', { icon: '🔒', duration: 5000 }); } catch {}
    setUser(null);
    // Timeout pequeno pro toast aparecer antes do redirect
    setTimeout(() => { window.location.href = '/login'; }, 400);
  }, []);

  /**
   * Inactivity watchdog — só ativo quando há sessão.
   * Reseta timer em qualquer evento de atividade (mouse/teclado/touch/scroll).
   * Após INACTIVITY_TIMEOUT_MS sem atividade, dispara logout.
   *
   * Notas:
   * - Ouvintes são passive pra não afetar performance de scroll.
   * - Timer é cancelado e re-armado em cada atividade (debounce implícito via clearTimeout).
   * - Pausar aba não "conta" atividade — correto pro caso de uso: se o usuário
   *   deixou o laptop aberto 1h, deve ser deslogado.
   */
  useEffect(() => {
    if (!user) return undefined;

    const resetTimer = () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = setTimeout(logoutInactivity, INACTIVITY_TIMEOUT_MS);
    };

    // Arma timer inicial e registra listeners
    resetTimer();
    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, resetTimer, { passive: true }));

    return () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, resetTimer));
    };
  }, [user, logoutInactivity]);

  const hasPermission = (perm) => {
    if (!user) return false;
    if (user.mode === SESSION_MODES.MASTER_GLOBAL) return true;
    return !!user.permissions?.includes(perm);
  };

  const hasFeature = (key) => {
    if (!user) return false;
    if (user.mode === SESSION_MODES.MASTER_GLOBAL) return true;
    const features = safeParseFeatures(user.tenant?.features);
    return !!features[key];
  };

  return <AuthContext.Provider value={{ user, loading, loginUser, logout, hasPermission, hasFeature, reloadUser: loadUser }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
